/**
 * Догадка о характеристиках по названию своего упражнения.
 *
 * Мастер создания предзаполняет ответы: человек написал «Сгибания ног с
 * резинкой» — мышца, снаряд и движение уже подсвечены, остаётся подтвердить.
 * Это правило по словам, а не ИИ: словаря хватает на типовые названия, а
 * ошибка здесь дешёвая — подсказку видно и её перебивают одним тапом.
 */

export interface TraitGuess {
  primary_muscle: string | null;
  equipment: string | null;
  movement: string | null;
}

type Rule = [stems: string[], value: string];

// Порядок важен: первое совпадение побеждает, поэтому частные фразы раньше
// общих слов — «бицепс бедра» раньше «бицепса», «жим ногами» раньше «жима».
const MUSCLE: Rule[] = [
  [["бицепс", "бедр"], "hamstrings"],
  [["сгибан", "ног"], "hamstrings"],
  [["румын"], "hamstrings"],
  [["гуд"], "hamstrings"],
  [["становая"], "hamstrings"],
  [["разгибан", "ног"], "quads"],
  [["жим", "ног"], "quads"],
  [["носк"], "calves"],
  [["икр"], "calves"],
  [["предплеч"], "forearms"],
  [["запяст"], "forearms"],
  [["шраг"], "traps"],
  [["трапец"], "traps"],
  [["трицепс"], "triceps"],
  [["француз"], "triceps"],
  [["бицепс"], "biceps"],
  [["молот"], "biceps"],
  [["широчайш"], "lats"],
  [["подтяг"], "lats"],
  [["вертикальн"], "lats"],
  [["груд"], "chest"],
  [["жим", "леж"], "chest"],
  [["свед"], "chest"],
  [["отжим"], "chest"],
  [["ягодиц"], "glutes"],
  [["ягодич"], "glutes"],
  [["мост"], "glutes"],
  [["плеч"], "delts"],
  [["дельт"], "delts"],
  [["отведен"], "delts"],
  [["пресс"], "abs"],
  [["скруч"], "abs"],
  [["планк"], "abs"],
  [["присед"], "quads"],
  [["выпад"], "quads"],
  [["квадр"], "quads"],
  [["тяга"], "back"],
  [["спин"], "back"],
];

const EQUIPMENT: Rule[] = [
  [["смит"], "smith"],
  [["гантел"], "dumbbell"],
  [["ez"], "ez_bar"],
  [["штанг"], "barbell"],
  [["гир"], "kettlebell"],
  [["тренаж"], "machine"],
  [["блок"], "cable"],
  [["кроссовер"], "cable"],
  [["канат"], "cable"],
  [["резин"], "band"],
  [["петл"], "band"],
  [["турник"], "bodyweight"],
  [["брус"], "bodyweight"],
  [["отжим"], "bodyweight"],
  [["подтяг"], "bodyweight"],
  [["планк"], "bodyweight"],
];

const MOVEMENT: Rule[] = [
  [["жим", "ног"], "squat"],
  [["становая"], "hinge"],
  [["румын"], "hinge"],
  [["гуд"], "hinge"],
  [["гиперэкст"], "hinge"],
  [["мост"], "hinge"],
  [["присед"], "squat"],
  [["выпад"], "squat"],
  [["жим"], "push"],
  [["отжим"], "push"],
  [["брус"], "push"],
  [["тяга"], "pull"],
  [["подтяг"], "pull"],
  [["прогулк"], "carry"],
  [["фермер"], "carry"],
  [["сгибан"], "isolation"],
  [["разгибан"], "isolation"],
  [["развед"], "isolation"],
  [["свед"], "isolation"],
  [["отведен"], "isolation"],
  [["шраг"], "isolation"],
  [["скруч"], "isolation"],
  [["подъем"], "isolation"],
];

const wordsOf = (name: string): string[] =>
  name
    .toLowerCase()
    .replace(/ё/g, "е")
    .split(/[^a-zа-я0-9]+/)
    .filter(Boolean);

function first(words: string[], rules: Rule[]): string | null {
  const hit = rules.find(([stems]) => stems.every((stem) => words.some((w) => w.startsWith(stem))));
  return hit ? hit[1] : null;
}

export function guessTraits(name: string): TraitGuess {
  const words = wordsOf(name);
  return {
    primary_muscle: first(words, MUSCLE),
    equipment: first(words, EQUIPMENT),
    movement: first(words, MOVEMENT),
  };
}
